import React, { useState, useEffect } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';

const UrlEncoderPage = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState('encode'); // 'encode' or 'decode'
  const [encodeType, setEncodeType] = useState('standard'); // 'standard', 'component', 'full'
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  // Encoding functions
  const encodeUrl = (text, type) => {
    try {
      setError('');
      switch (type) {
        case 'standard':
          // encodeURI - doesn't encode: A-Z a-z 0-9 - _ . ! ~ * ' ( ) ; / ? : @ & = + $ , #
          return encodeURI(text);
        case 'component':
          // encodeURIComponent - doesn't encode: A-Z a-z 0-9 - _ . ! ~ * ' ( )
          return encodeURIComponent(text);
        case 'full':
          // Encode everything except alphanumeric
          return text.split('').map(char => {
            if (/[a-zA-Z0-9]/.test(char)) {
              return char;
            }
            return '%' + char.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0');
          }).join('');
        default:
          return encodeURIComponent(text);
      }
    } catch (e) {
      setError('Error encoding: ' + e.message);
      return '';
    }
  };

  // Decoding functions
  const decodeUrl = (text, type) => {
    try {
      setError('');
      switch (type) {
        case 'standard':
          return decodeURI(text);
        case 'component':
        case 'full':
          return decodeURIComponent(text);
        default:
          return decodeURIComponent(text);
      }
    } catch (e) {
      setError('Error decoding: Invalid encoded string. ' + e.message);
      return '';
    }
  };

  // Process input based on mode
  const processInput = () => {
    if (!input) {
      setOutput('');
      setError('');
      return;
    }

    if (mode === 'encode') {
      setOutput(encodeUrl(input, encodeType));
    } else {
      setOutput(decodeUrl(input, encodeType));
    }
  };

  // Auto-update when inputs change
  useEffect(() => {
    if (autoUpdate) {
      const timeoutId = setTimeout(() => {
        processInput();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, mode, encodeType, autoUpdate]);

  // Swap input and output
  const swapInputOutput = () => {
    const temp = input;
    setInput(output);
    setOutput(temp);
    setMode(mode === 'encode' ? 'decode' : 'encode');
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Clear all
  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  // Sample URLs
  const sampleUrls = [
    { label: 'URL with spaces', value: 'https://example.com/search?q=hello world&lang=en' },
    { label: 'Special characters', value: 'https://example.com/path?name=José&city=São Paulo' },
    { label: 'Query parameters', value: 'name=John Doe&email=john@example.com&message=Hello, how are you?' },
    { label: 'Unicode text', value: 'こんにちは世界' },
    { label: 'JSON in URL', value: '{"name":"John","age":30}' },
    { label: 'Complex URL', value: 'https://api.example.com/v1/users?filter=status:active&sort=name:asc&page=1' }
  ];

  // Encoding types info
  const encodingTypes = [
    {
      value: 'standard',
      label: 'Standard (encodeURI)',
      description: 'Encodes special characters but preserves URL structure (: / ? # @ etc.)',
      example: 'hello world → hello%20world'
    },
    {
      value: 'component',
      label: 'Component (encodeURIComponent)',
      description: 'Encodes all special characters. Use for query parameter values.',
      example: 'a=b&c=d → a%3Db%26c%3Dd'
    },
    {
      value: 'full',
      label: 'Full Encoding',
      description: 'Encodes everything except alphanumeric characters (A-Z, a-z, 0-9).',
      example: 'hello! → hello%21'
    }
  ];

  return (
    <div>
      <SEO 
        title="URL Encoder/Decoder - Encode and Decode URLs Online" 
        description="Free online URL encoder and decoder. Encode special characters in URLs or decode percent-encoded strings. Supports encodeURI and encodeURIComponent."
        keywords="url encoder, url decoder, percent encoding, encodeuri, encodeuricomponent, url escape, url unescape, online url encoder"
      />
      
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>URL Encoder / Decoder</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Encode special characters in URLs or decode percent-encoded strings. All processing happens locally in your browser.
        </p>
        
        <AdUnit />

        {/* Mode Toggle */}
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={() => setMode('encode')}
              style={{
                flex: '0 0 200px',
                padding: '14px 24px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: mode === 'encode' ? '#2196f3' : '#e0e0e0',
                color: mode === 'encode' ? 'white' : '#333',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
            >
              🔒 Encode
            </button>
            <button
              onClick={swapInputOutput}
              style={{
                padding: '14px 20px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: '#333',
                cursor: 'pointer',
                fontSize: '16px'
              }}
              title="Swap input and output"
            >
              ⇄
            </button>
            <button
              onClick={() => setMode('decode')}
              style={{
                flex: '0 0 200px',
                padding: '14px 24px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: mode === 'decode' ? '#4caf50' : '#e0e0e0',
                color: mode === 'decode' ? 'white' : '#333',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
            >
              🔓 Decode
            </button>
          </div>
        </div>

        {/* Encoding Type Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
            Encoding Type:
          </label>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {encodingTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => setEncodeType(type.value)}
                style={{
                  padding: '10px 20px',
                  border: `2px solid ${encodeType === type.value ? '#2196f3' : '#ddd'}`,
                  borderRadius: '4px',
                  backgroundColor: encodeType === type.value ? '#e3f2fd' : 'white',
                  color: '#333',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
              >
                {type.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: '13px', color: '#666', marginTop: '8px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <strong>{encodingTypes.find(t => t.value === encodeType)?.label}:</strong>{' '}
            {encodingTypes.find(t => t.value === encodeType)?.description}
            <br />
            <code style={{ fontSize: '12px', color: '#1976d2' }}>
              {encodingTypes.find(t => t.value === encodeType)?.example}
            </code>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Input Section */}
          <div className="input-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ fontWeight: '500' }}>
                {mode === 'encode' ? 'Text to Encode:' : 'URL to Decode:'}
              </label>
              <span style={{ fontSize: '12px', color: '#666' }}>{input.length} characters</span>
            </div>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={mode === 'encode' 
                ? 'Enter text or URL to encode...' 
                : 'Enter encoded URL to decode...'}
              style={{
                width: '100%',
                height: '250px',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'monospace',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
            
            {/* Sample URLs */}
            <div style={{ marginTop: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Quick Examples:
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {sampleUrls.map((sample, index) => (
                  <button
                    key={index}
                    onClick={() => setInput(sample.value)}
                    style={{
                      padding: '4px 10px',
                      border: '1px solid #ddd',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      color: '#333',
                      cursor: 'pointer',
                      fontSize: '11px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    {sample.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="output-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ fontWeight: '500' }}>
                {mode === 'encode' ? 'Encoded Result:' : 'Decoded Result:'}
              </label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>{output.length} characters</span>
                {output && (
                  <button
                    onClick={copyToClipboard}
                    style={{
                      padding: '4px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: copied ? '#4caf50' : 'white',
                      color: copied ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '12px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {copied ? '✓ Copied!' : '📋 Copy'}
                  </button>
                )}
              </div>
            </div>
            <textarea
              value={output}
              readOnly
              placeholder={mode === 'encode' 
                ? 'Encoded result will appear here...' 
                : 'Decoded result will appear here...'}
              style={{
                width: '100%',
                height: '250px',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'monospace',
                resize: 'vertical',
                boxSizing: 'border-box',
                backgroundColor: '#f9f9f9'
              }}
            />
            
            {/* Error Message */}
            {error && (
              <div style={{
                marginTop: '10px',
                padding: '10px',
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '4px',
                color: '#c62828',
                fontSize: '13px'
              }}>
                ⚠️ {error}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={processInput}
            style={{
              padding: '12px 24px',
              backgroundColor: mode === 'encode' ? '#2196f3' : '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {mode === 'encode' ? '🔒 Encode Now' : '🔓 Decode Now'}
          </button>
          
          <button
            onClick={clearAll}
            style={{
              padding: '12px 24px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            🗑️ Clear
          </button>

          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginLeft: '20px' }}>
            <input
              type="checkbox"
              checked={autoUpdate}
              onChange={(e) => setAutoUpdate(e.target.checked)}
              style={{ marginRight: '8px', width: '16px', height: '16px' }}
            />
            <span style={{ fontSize: '14px' }}>Auto-update</span>
          </label>
        </div>

        {/* Character Reference Table */}
        <div className="reference-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>Common URL Encodings</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Character</th>
                  <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Encoded</th>
                  <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Description</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { char: ' ', encoded: '%20', desc: 'Space' },
                  { char: '!', encoded: '%21', desc: 'Exclamation mark' },
                  { char: '#', encoded: '%23', desc: 'Hash/Fragment' },
                  { char: '$', encoded: '%24', desc: 'Dollar sign' },
                  { char: '%', encoded: '%25', desc: 'Percent sign' },
                  { char: '&', encoded: '%26', desc: 'Ampersand' },
                  { char: '+', encoded: '%2B', desc: 'Plus sign' },
                  { char: '/', encoded: '%2F', desc: 'Forward slash' },
                  { char: '=', encoded: '%3D', desc: 'Equals sign' },
                  { char: '?', encoded: '%3F', desc: 'Question mark' },
                  { char: '@', encoded: '%40', desc: 'At sign' }
                ].map((item, index) => (
                  <tr key={index}>
                    <td style={{ padding: '8px 10px', border: '1px solid #ddd', fontFamily: 'monospace' }}>{item.char}</td>
                    <td style={{ padding: '8px 10px', border: '1px solid #ddd', fontFamily: 'monospace', color: '#1976d2' }}>{item.encoded}</td>
                    <td style={{ padding: '8px 10px', border: '1px solid #ddd' }}>{item.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Information Section */}
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>About URL Encoding</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>What is URL Encoding?</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                URL encoding (also known as percent-encoding) converts characters into a format that can be transmitted
                over the Internet. Characters that are not allowed in URLs are replaced with a % followed by two hexadecimal digits.
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>When to Use</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                Use <strong>encodeURI</strong> for complete URLs to preserve the URL structure.
                Use <strong>encodeURIComponent</strong> for query parameter values or when encoding
                individual parts of a URL.
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Safe Characters</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                Letters (A-Z, a-z), digits (0-9), and the characters - _ . ~ are considered safe and don't need encoding.
                Reserved characters like : / ? # @ have special meaning in URLs.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .main-content {
            grid-template-columns: 1fr !important;
            gap: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default UrlEncoderPage;

